import { Button } from "@prelude-ai/design-system"
import {
  createRootRoute,
  Link,
  Outlet,
  redirect,
  useLoaderData,
  useMatchRoute,
  useRouter,
  useSearch
} from "@tanstack/react-router"
import { Bug, Plus } from "lucide-react"
import { useCallback, useMemo } from "react"
import { z } from "zod"
import type { AgentMetadata as Agent } from "../../types/index.ts"
import AgentSelector from "../components/AgentSelector"
import { ConversationSelector } from "../components/ConversationSelector.tsx"
import { ModeToggle } from "../components/ModeToggle"
import ToolsDrawer from "../components/ToolsDrawer"
import { API_BASE_URL, HAS_PRELUDE_SESSION, USE_STAGING } from "../config"
import { PendingMessageProvider } from "../contexts/PendingMessageContext.tsx"
import paintLogo from "../icons/paint-logo.png"
import { toast } from "../utils/toast.tsx"

const rootSearchSchema = z.object({
  devTools: z.boolean().optional(),
  agent: z.string().optional(),
  backgroundMode: z.boolean().optional(),
  initialFlowContext: z.string().optional()
})

type RootSearch = Partial<z.output<typeof rootSearchSchema>>

function RootLayout() {
  const { devTools, agent, backgroundMode } = useSearch({ from: "__root__" })
  const { navigate, state } = useRouter()
  const { agents, authenticated } = useLoaderData({ from: "__root__" })

  const matchRoute = useMatchRoute()
  const conversationRoute = matchRoute({ to: "/conversations/$conversationId" })
  const viewRoute = matchRoute({ to: "/conversations/view/$conversationId" })
  const activeConversationId =
    (conversationRoute ? conversationRoute.conversationId : undefined) ||
    (viewRoute ? viewRoute.conversationId : undefined)
  const handleToggleDevTools = useCallback(() => {
    navigate({
      to: state.location.pathname,
      search: (prev: RootSearch) => ({ ...prev, devTools: devTools ? undefined : true })
    })
  }, [devTools, navigate, state.location.pathname])

  const handleBackgroundModeChange = useCallback(() => {
    const enabling = !backgroundMode
    navigate({
      to: ".",
      search: (prev: RootSearch) => ({ ...prev, backgroundMode: enabling ? true : undefined })
    })
    if (enabling) {
      toast.warning("Background mode enabled", {
        description: "The agent will run without asking for user input"
      })
    }
  }, [backgroundMode, navigate])

  const handleSelectAgent = useCallback(
    (agentName: string) => {
      // If we're on a conversation page, switching agents should go to /new
      if (state.location.pathname.startsWith("/conversations/")) {
        navigate({
          to: "/new",
          search: (prev: RootSearch) => ({ ...prev, agent: agentName })
        })
      } else {
        navigate({
          to: ".",
          search: (prev: RootSearch) => ({ ...prev, agent: agentName })
        })
      }
    },
    [navigate, state.location.pathname]
  )

  const AgentToolDrawer = useMemo(() => {
    const currentAgent = agents.find((a) => a.name === agent)
    return currentAgent ? <ToolsDrawer tools={currentAgent.tools} agentName={currentAgent.name} /> : null
  }, [agents, agent])

  if (!authenticated) {
    return (
      <div className="flex h-screen min-h-screen flex-col overflow-hidden">
        <Outlet />
      </div>
    )
  }

  return (
    <PendingMessageProvider>
      <div className="flex h-screen min-h-screen flex-col overflow-hidden">
        <header className="flex flex-row items-center gap-2 p-3 sm:p-4">
          <img src={paintLogo} alt="Logo" className="w-9 shrink-0" />
          <h1 className="text-lg font-mono">Agent Studio</h1>
          <div className="ml-auto flex items-center gap-2">
            <Button variant="outline" size="sm" className="gap-1" asChild>
              <Link to="/new" search={(prev: RootSearch) => prev}>
                <Plus className="h-4 w-4" />
                <span className="max-sm:hidden">New Chat</span>
              </Link>
            </Button>
            <Button
              variant={devTools ? "default" : "outline"}
              size="sm"
              className="gap-1"
              onClick={handleToggleDevTools}
            >
              <Bug className="h-4 w-4" />
              <span className="hidden sm:inline">Dev Tools</span>
            </Button>
            <ModeToggle />
          </div>
        </header>
        <div className="border-b flex justify-between pb-2 px-2">
          <div className="flex flex-row sm:flex-nowrap flex-wrap sm:max-w-full max-w-2 items-center sm:gap-2 gap-2">
            {agent && (
              <ConversationSelector
                agent={agent}
                activeConversationId={activeConversationId}
                isNavigating={state.status === "pending"}
              />
            )}
            <AgentSelector
              agents={agents}
              selectedAgent={agent ?? null}
              onSelectAgent={handleSelectAgent}
              backgroundMode={!!backgroundMode}
              onBackgroundModeChange={handleBackgroundModeChange}
            />
          </div>
          {AgentToolDrawer}
        </div>
        <Outlet />
      </div>
    </PendingMessageProvider>
  )
}

export const Route = createRootRoute({
  validateSearch: rootSearchSchema,
  component: RootLayout,
  loaderDeps: ({ search: { agent } }) => {
    return { agent }
  },
  loader: async ({ location }) => {
    if (location.pathname === "/login") {
      return { agents: [], authenticated: false }
    }

    if (USE_STAGING && !HAS_PRELUDE_SESSION) {
      throw redirect({ to: "/login", search: { error: undefined } })
    }
    const res = await fetch(`${API_BASE_URL}/api/agents`, { credentials: "include" })
    if (!res.ok && res.status === 401) {
      throw redirect({ to: "/login", search: { error: undefined } })
    }

    const data = (await res.json()) as Record<"agents", Agent[]>

    return { agents: data.agents, authenticated: true }
  }
})
